/**
 * Admin Dashboard Data Structures
 * For Creative Queue internal team members only
 */

// Organization status tracking
export enum OrganizationStatus {
  PENDING = 'pending', // Created but admin hasn't claimed
  ACTIVE = 'active', // Admin claimed and organization is active
  INACTIVE = 'inactive', // Suspended or deactivated
  DEMO = 'demo', // Demo organization for sales
}

// User onboarding status
export enum UserOnboardingStatus {
  INVITED = 'invited', // Invitation sent
  CLAIMED = 'claimed', // User claimed account but hasn't completed setup
  ACTIVE = 'active', // User is active and has completed setup
  INACTIVE = 'inactive', // User account is inactive
}

// Plan limits
export interface PlanLimits {
  seats: number; // Maximum number of users
  storageGB: number; // Maximum storage in GB
  projects: number; // Maximum number of projects/sessions
}

// Usage tracking
export interface OrganizationUsage {
  seats: number; // Current number of users
  storageBytes: number; // Current storage usage in bytes
  projects: number; // Current number of projects/sessions
  lastUpdated: Date;
}

// Organization claim token
export interface OrganizationClaimToken {
  token: string; // Secure random token
  organizationId: string;
  adminEmail: string;
  createdAt: Date;
  expiresAt: Date;
  claimedAt?: Date;
  claimedBy?: {
    userId: string;
    email: string;
  };
  status: 'pending' | 'claimed' | 'expired';
}

// Extended organization data for admin dashboard
export interface AdminOrganization {
  id: string;
  name: string;
  status: OrganizationStatus;
  createdAt: Date;
  createdBy: {
    userId: string;
    email: string | null;
  };

  // Admin info
  adminEmail: string;
  adminName?: string;

  // Billing
  stripeBillingLink?: string;

  // Plan and usage
  planLimits: PlanLimits;
  usage: OrganizationUsage;

  // Metadata
  isDemo: boolean;
  notes?: string; // Internal notes for CQ team
}

// User invitation tracking
export interface UserInvitationStatus {
  userId?: string; // Only set after user claims account
  email: string;
  name: string;
  organizationId: string;
  organizationName: string;

  status: UserOnboardingStatus;

  invitedAt: Date;
  invitedBy: {
    userId: string;
    email: string | null;
  };

  claimedAt?: Date;
  lastActivityAt?: Date;

  // Setup progress
  setupSteps: {
    accountClaimed: boolean;
    profileCompleted: boolean;
    firstProjectCreated: boolean;
    firstMemberInvited: boolean;
  };
}

// Admin action audit log
export interface AdminAuditLog {
  id: string;
  action: AdminAction;
  performedBy: {
    userId: string;
    email: string | null;
    name: string | null;
  };
  performedAt: Date;

  // Target
  targetType: 'organization' | 'user' | 'member' | 'claim_token' | 'session';
  targetId: string;
  targetEmail?: string;

  // Details
  details: Record<string, unknown>;

  // Result
  success: boolean;
  errorMessage?: string;
}

export enum AdminAction {
  // Organization actions
  CREATE_ORGANIZATION = 'create_organization',
  UPDATE_ORGANIZATION = 'update_organization',
  DELETE_ORGANIZATION = 'delete_organization',
  GENERATE_CLAIM_TOKEN = 'generate_claim_token',
  POPULATE_DEMO_DATA = 'populate_demo_data',

  // User actions
  INVITE_USER = 'invite_user',
  RESEND_INVITATION = 'resend_invitation',
  DEACTIVATE_USER = 'deactivate_user',
  REACTIVATE_USER = 'reactivate_user',

  // Member actions
  ADD_MEMBER = 'add_member',
  REMOVE_MEMBER = 'remove_member',
  CHANGE_MEMBER_ROLE = 'change_member_role',

  // Support actions
  TROUBLESHOOT_ACCESS = 'troubleshoot_access',
  UPDATE_USAGE_LIMITS = 'update_usage_limits',

  // Session actions (CRE-253)
  DELETE_SESSION = 'delete_session',
  RESTORE_SESSION = 'restore_session',
  TRANSFER_SESSION_OWNERSHIP = 'transfer_session_ownership',
  REQUEST_SESSION_DELETION = 'request_session_deletion',
  REQUEST_SESSION_OWNERSHIP_TRANSFER = 'request_session_ownership_transfer',
}

// CQ Admin user permissions
export interface CQAdminUser {
  userId: string;
  email: string;
  name: string;
  role: CQAdminRole;
  addedAt: Date;
  addedBy: {
    userId: string;
    email: string | null;
  };
}

export enum CQAdminRole {
  SUPER_ADMIN = 'super_admin', // Full access to all features
  SALES = 'sales', // Can create orgs, generate tokens, manage demo data
  SUPPORT = 'support', // Can view and help with user issues
  ENGINEERING = 'engineering', // Full access for troubleshooting
}
